import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
  EntityContentLayoutBlueprint,
  EntityHeaderBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import {
  useAsyncEntity,
  entityRouteRef,
  EntityDisplayName,
  FavoriteEntity,
  InspectEntityDialog,
  UnregisterEntityDialog,
} from '@backstage/plugin-catalog-react';
import { Header, HeaderLabel } from '@backstage/core-components';
import { useRouteRefParams } from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import BugReportIcon from '@material-ui/icons/BugReport';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import DeleteIcon from '@material-ui/icons/Delete';
import {
  EntityCatalogGraphCard,
  Direction,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
  parseEntityRef,
} from '@backstage/catalog-model';

// Custom header showing kind as the type label, with owner/lifecycle
// metadata on the right side plus context menu, favorites, and dialogs.
const CustomEntityHeader = () => {
  const { entity } = useAsyncEntity();
  const { kind, name } = useRouteRefParams(entityRouteRef);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const displayName = entity?.metadata.title ?? entity?.metadata.name ?? name;

  // Header metadata labels (right side)
  const ownerRef = entity?.relations?.find(
    r => r.type === RELATION_OWNED_BY,
  )?.targetRef;
  const ownerParsed = ownerRef ? parseEntityRef(ownerRef) : undefined;
  const ownerName = ownerParsed?.name.replace(/-/g, ' ');
  const ownerUrl = ownerParsed
    ? `/catalog/${ownerParsed.namespace ?? 'default'}/${ownerParsed.kind}/${ownerParsed.name}`
    : undefined;
  const lifecycle =
    entity?.spec && 'lifecycle' in entity.spec
      ? String(entity.spec.lifecycle)
      : undefined;

  // Context menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  );
  const closeMenu = useCallback(() => setAnchorEl(null), []);

  // Unregister dialog
  const [unregisterOpen, setUnregisterOpen] = useState(false);
  useEffect(() => setUnregisterOpen(false), [location.pathname]);

  // Inspect dialog
  const inspectTab = searchParams.get('inspect');
  const inspectOpen = typeof inspectTab === 'string';

  return (
    <Header
      pageTitleOverride={displayName}
      type={kind.toLocaleLowerCase('en-US')}
      title={
        <Box
          display="inline-flex"
          alignItems="center"
          height="1em"
          maxWidth="100%"
        >
          <Box
            component="span"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            overflow="hidden"
          >
            {entity ? (
              <EntityDisplayName entityRef={entity} hideIcon />
            ) : (
              displayName
            )}
          </Box>
          {entity && <FavoriteEntity entity={entity} />}
        </Box>
      }
    >
      {entity && (
        <>
          {ownerName && (
            <HeaderLabel label="Owner" value={ownerName} url={ownerUrl} />
          )}
          {lifecycle && <HeaderLabel label="Lifecycle" value={lifecycle} />}
          <IconButton aria-label="More" color="inherit" onClick={openMenu}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeMenu}
          >
            <MenuItem
              onClick={() => {
                closeMenu();
                setSearchParams('inspect');
              }}
            >
              <ListItemIcon>
                <BugReportIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Inspect entity" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                navigator.clipboard.writeText(window.location.href);
              }}
            >
              <ListItemIcon>
                <FileCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Copy entity URL" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeMenu();
                setUnregisterOpen(true);
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Unregister entity" />
            </MenuItem>
          </Menu>
          <InspectEntityDialog
            entity={entity}
            open={inspectOpen}
            initialTab={
              inspectTab as
                | 'overview'
                | 'ancestry'
                | 'colocated'
                | 'json'
                | 'yaml'
                | undefined
            }
            onClose={() => setSearchParams()}
            onSelect={tab => setSearchParams(`inspect=${tab}`)}
          />
          <UnregisterEntityDialog
            entity={entity}
            open={unregisterOpen}
            onClose={() => setUnregisterOpen(false)}
            onConfirm={async () => {
              setUnregisterOpen(false);
              navigate('/catalog');
            }}
          />
        </>
      )}
    </Header>
  );
};

const customEntityHeader = EntityHeaderBlueprint.make({
  name: 'custom',
  params: {
    loader: async () => <CustomEntityHeader />,
  },
});

// Custom layout that restores a 2-column grid from the old EntityPage.tsx.
// Each card gets its own md=6 cell so they flow into a 2x2 grid naturally.
function GridEntityContentLayout({
  cards,
}: {
  cards: { element: React.JSX.Element; type?: string }[];
}) {
  return (
    <Grid container spacing={3} alignItems="flex-start">
      {cards.map((card, i) => (
        <Grid key={i} item md={6} xs={12}>
          {card.element}
        </Grid>
      ))}
    </Grid>
  );
}

const customOverviewLayout = EntityContentLayoutBlueprint.make({
  name: 'grid-layout',
  params: {
    loader: async () => GridEntityContentLayout,
  },
});

const systemDiagramContent = EntityContentBlueprint.make({
  name: 'diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    filter: { kind: 'system' },
    loader: async () => (
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    ),
  },
});

// Filtered relationship cards that hide when the entity has no relevant data.
// These replace the defaults (disabled in app-config.yaml) which always render
// empty-state messages even when there are zero relations.
const filteredDependsOnComponents = EntityCardBlueprint.make({
  name: 'depends-on-components-filtered',
  params: {
    filter: entity =>
      entity.kind.toLowerCase() === 'component' &&
      (entity.relations?.some(
        r =>
          r.type === RELATION_DEPENDS_ON &&
          r.targetRef.startsWith('component:'),
      ) ??
        false),
    loader: async () =>
      import('@backstage/plugin-catalog').then(m => (
        <m.EntityDependsOnComponentsCard variant="gridItem" />
      )),
  },
});

const filteredDependsOnResources = EntityCardBlueprint.make({
  name: 'depends-on-resources-filtered',
  params: {
    filter: entity =>
      entity.kind.toLowerCase() === 'component' &&
      (entity.relations?.some(
        r =>
          r.type === RELATION_DEPENDS_ON && r.targetRef.startsWith('resource:'),
      ) ??
        false),
    loader: async () =>
      import('@backstage/plugin-catalog').then(m => (
        <m.EntityDependsOnResourcesCard variant="gridItem" />
      )),
  },
});

const filteredHasSubcomponents = EntityCardBlueprint.make({
  name: 'has-subcomponents-filtered',
  params: {
    filter: entity =>
      entity.kind.toLowerCase() === 'component' &&
      (entity.relations?.some(r => r.type === RELATION_HAS_PART) ?? false),
    loader: async () =>
      import('@backstage/plugin-catalog').then(m => (
        <m.EntityHasSubcomponentsCard variant="gridItem" />
      )),
  },
});

const filteredProvidedApis = EntityCardBlueprint.make({
  name: 'provided-apis-filtered',
  params: {
    filter: entity =>
      entity.kind.toLowerCase() === 'component' &&
      (entity.relations?.some(r => r.type === RELATION_PROVIDES_API) ?? false),
    loader: async () =>
      import('@backstage/plugin-api-docs').then(m => (
        <m.EntityProvidedApisCard variant="gridItem" />
      )),
  },
});

const filteredConsumedApis = EntityCardBlueprint.make({
  name: 'consumed-apis-filtered',
  params: {
    filter: entity =>
      entity.kind.toLowerCase() === 'component' &&
      (entity.relations?.some(r => r.type === RELATION_CONSUMES_API) ?? false),
    loader: async () =>
      import('@backstage/plugin-api-docs').then(m => (
        <m.EntityConsumedApisCard variant="gridItem" />
      )),
  },
});

export const catalogEntityModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    customOverviewLayout,
    systemDiagramContent,
    customEntityHeader,
    filteredDependsOnComponents,
    filteredDependsOnResources,
    filteredHasSubcomponents,
    filteredProvidedApis,
    filteredConsumedApis,
  ],
});
